import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  TextField,
  IconButton,
  Stack,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { EstimateDto, UNIT_CHOICES } from "./types.ts";
import { AmountField } from "./AmountField.tsx";

interface ItemRowFieldsProps {
  index: number;
  /**
   * Label for the trailing remark/note column.
   *
   * jinyoung & mun pass "비고" (or omit to use default).
   * comparison passes "품목코드/비고".
   */
  remarkLabel?: string;
  onRemove: () => void;
}

/**
 * Inner Controllers for a single item row.
 *
 * Renders 품명 / 규격 / 단위(Autocomplete) / 수량 / 단가 / AmountField /
 * 비고 / 삭제 inside a horizontal `<Stack>`.
 *
 * Must be rendered inside an existing `FormProvider<EstimateDto>`.
 */
export default function ItemRowFields({
  index,
  remarkLabel = "비고",
  onRemove,
}: ItemRowFieldsProps) {
  const { control } = useFormContext<EstimateDto>();

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">

      {/* ── ① 품명 ── */}
      <Controller
        name={`content.items.${index}.item`}
        control={control}
        rules={{ required: "품명을 입력하세요" }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="품명"
            error={!!fieldState.error}
            helperText={"품명을 입력하세요"}
            sx={{ minWidth: 200 }}
          />
        )}
      />

      {/* ── ② 규격 ── */}
      <Controller
        name={`content.items.${index}.size`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="규격"
            sx={{ minWidth: 200 }}
            helperText={"규격을 입력하세요"}
          />
        )}
      />

      {/* ── ③ 단위 (select 박스로 변경 예시) ── */}
      <Controller
        name={`content.items.${index}.unit`}
        control={control}
        defaultValue=""
        rules={{ required: "단위를 입력하세요" }}
        render={({ field, fieldState }) => (
          <Autocomplete
            freeSolo
            options={UNIT_CHOICES}
            value={field.value}
            onChange={(_, newVal) =>
              field.onChange(newVal ?? "")
              // handler
            }
            onInputChange={(_, newInput) =>
              field.onChange(newInput)
              // handler
            }
            renderInput={(params) =>
              <TextField
                {...params}
                label="단위"
                error={!!fieldState.error}
                helperText={"단위를 입력하세요"}
                sx={{ width: 200 }}
              />
            }
          />
        )}
      />

      {/* ── ④ 수량 (숫자 전용) ── */}
      <Controller
        name={`content.items.${index}.qty`}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            type="text"
            inputMode="numeric"
            label="수량"
            error={!!fieldState.error}
            sx={{ width: 80 }}
            onKeyDown={(e) => {
              if (/^\d$/.test(e.key) && field.value === 0) {
                e.preventDefault();
                field.onChange(Number(e.key));
              }
            }}
            onChange={(e) => {
              if (e.target.value === "-" || e.target.value === "") {
                field.onChange(e.target.value);
              } else {
                field.onChange(Number(e.target.value));
              }
            }}
          />
        )}
      />

      {/* ── ⑤ 단가 ── */}
      <Controller
        name={`content.items.${index}.unitPrice`}
        control={control}
        render={({ field, fieldState }) => {
          /* ① 화면에 보여 줄 값: 0 → "" , 그 외는 3자리 콤마 */
          const display = field.value === 0 || field.value === undefined
            ? ""
            : Number(field.value).toLocaleString("ko-KR");  // 12,345 처럼

          return (
            <TextField
              label="단가"
              placeholder="0"
              sx={{ minWidth: 200 }}
              error={!!fieldState.error}
              /** ② 타입 text 로 변경 (콤마 허용) */
              type="text"
              inputMode="numeric"          /* 모바일 숫자 키패드 */
              value={display}
              onKeyDown={(e) => {
                /* 첫 키 입력 시 0 덮어쓰기(앞서 구현했던 로직) */
                if (/^\d$/.test(e.key) && field.value === 0) {
                  e.preventDefault();
                  field.onChange(Number(e.key));
                }
              }}
              onChange={(e) => {
                /* ③ 콤마 제거 후 숫자로 변환하여 저장 */
                const raw = e.target.value.replace(/,/g, "");
                field.onChange(raw === "" ? 0 : Number(raw));
              }}
              onFocus={(e) => e.target.select()}  /* 클릭하면 전체 선택 */
            />
          );
        }}
      />


      <AmountField key={`amount-${index}`} index={index} />

      {/* ── ⑦ 비고 ── */}
      <Controller
        name={`content.items.${index}.remark`}
        control={control}
        render={({ field }) => (
          <TextField {...field} label={remarkLabel} sx={{ minWidth: 200 }} />
        )}
      />

      {/* 삭제 버튼 */}
      <IconButton onClick={onRemove}>
        <DeleteIcon />
      </IconButton>
    </Stack>
  );
}
